import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, X, Image as ImageIcon, Crop } from 'lucide-react';
import api from '../services/api';
import { fetchBrands as loadBrands, fetchCategories as loadCategories } from '../services/adminCatalog';
import { getEntityId } from '../utils/entityId';
import { Checkbox } from '../components/ui';

interface CreateProductForm {
  name: string;
  description: string;
  brand: string;
  category: string;
  productType: 'simple' | 'variable';
  price?: number;
  originalPrice?: number;
  condition?: 'Brand New' | 'Used' | 'Refurbished';
  color?: string;
  quantityAvailable?: number;
  images: string[];
  isActive: boolean;
  isRecommended: boolean;
  isFlash: boolean;
}

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState<{id: string; name: string; parent?: string | null; attributes?: any[]}[]>([]);
  const [brands, setBrands] = useState<{id: string; name: string}[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<{url: string, index: number} | null>(null);
  const [categoryAttributes, setCategoryAttributes] = useState<any[]>([]);
  const [attributeValues, setAttributeValues] = useState<{[key: string]: any[]}>({});
  const [selectedAttributes, setSelectedAttributes] = useState<{[key: string]: string}>({});
  const [formData, setFormData] = useState<CreateProductForm>({
    name: '',
    description: '',
    brand: '',
    category: '',
    productType: 'simple',
    price: undefined,
    originalPrice: undefined,
    condition: 'Brand New',
    color: '',
    quantityAvailable: undefined,
    images: [],
    isActive: true,
    isRecommended: false,
    isFlash: false,
  });

  const fetchCategories = async () => {
    try {
      const list = await loadCategories<{ id: string; name: string; parent?: string | null; attributes?: any[] }>();
      setCategories(list);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Helper function to get categories for product selection (only leaf categories or categories without subcategories)
  const getSelectableCategories = () => {
    const allCategories = categories;
    const categoriesWithChildren = new Set(
      allCategories.filter(cat => cat.parent).map(cat => cat.parent)
    );
    
    // Return categories that don't have children (leaf categories)
    return allCategories.filter(cat => !categoriesWithChildren.has(cat.id));
  };

  // Helper function to format category name with parent hierarchy
  const formatCategoryName = (category: any) => {
    const parent = categories.find(cat => cat.id === category.parent);
    return parent ? `${parent.name} > ${category.name}` : category.name;
  };

  const fetchBrands = async () => {
    try {
      const list = await loadBrands<{ id: string; name: string }>();
      setBrands(list.map((b) => ({ id: getEntityId(b), name: b.name })));
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchCategoryAttributes = async (categoryId: string) => {
    try {
      // Fetch category details with populated attributes
      const response = await api.get(`/admin/categories/${categoryId}`);
      const category = response.data.data;

      if (category.attributes && category.attributes.length > 0) {
        setCategoryAttributes(category.attributes);

        // Fetch values for each attribute
        const valuesPromises = category.attributes.map(async (catAttr: any) => {
          const attrId = typeof catAttr.attribute === 'object' ? catAttr.attribute._id : catAttr.attribute;
          const valuesResponse = await api.get(`/admin/attribute-values/attribute/${attrId}`);
          return { attributeId: attrId, values: valuesResponse.data.data };
        });

        const allValues = await Promise.all(valuesPromises);
        const valuesMap: {[key: string]: any[]} = {};
        allValues.forEach(item => {
          valuesMap[item.attributeId] = item.values;
        });
        setAttributeValues(valuesMap);
      } else {
        setCategoryAttributes([]);
        setAttributeValues({});
      }
      setSelectedAttributes({});
    } catch (error) {
      console.error('Error fetching category attributes:', error);
      setCategoryAttributes([]);
      setAttributeValues({});
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setFormData({ ...formData, category: categoryId });
    if (categoryId) {
      fetchCategoryAttributes(categoryId);
    } else {
      setCategoryAttributes([]);
      setAttributeValues({});
      setSelectedAttributes({});
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.description.trim() || !formData.brand || !formData.category) {
      alert(t('messages.requiredFields', { ns: 'addProduct' }));
      return;
    }

    if (formData.images.length === 0) {
      alert(t('messages.imageRequired', { ns: 'addProduct', defaultValue: 'Please add at least one product image.' }));
      return;
    }

    if (formData.productType === 'variable') {
      alert(
        t('messages.variableNotSupported', {
          ns: 'addProduct',
          defaultValue: 'Variable products must be created from the seller dashboard.',
        })
      );
      return;
    }

    const price = formData.price !== undefined ? Number(formData.price) : NaN;
    const quantityAvailable =
      formData.quantityAvailable !== undefined ? Number(formData.quantityAvailable) : NaN;

    if (!Number.isFinite(price) || price < 0) {
      alert(t('messages.priceRequired', { ns: 'addProduct', defaultValue: 'Please enter a valid price.' }));
      return;
    }

    if (!Number.isFinite(quantityAvailable) || quantityAvailable < 0) {
      alert(
        t('messages.quantityRequired', {
          ns: 'addProduct',
          defaultValue: 'Please enter a valid quantity.',
        })
      );
      return;
    }

    // Validate required attributes
    const requiredAttrs = categoryAttributes.filter((catAttr: any) => catAttr.isRequired);
    for (const catAttr of requiredAttrs) {
      const attrId = typeof catAttr.attribute === 'object' ? catAttr.attribute._id : catAttr.attribute;
      if (!selectedAttributes[attrId]) {
        const attrName = typeof catAttr.attribute === 'object' ? catAttr.attribute.displayName : 'This attribute';
        alert(t('messages.attributeRequired', { ns: 'addProduct', attributeName: attrName }));
        return;
      }
    }

    setSubmitting(true);
    try {
      // Format attributes for submission
      const attributes = Object.entries(selectedAttributes).map(([attributeId, valueId]) => ({
        attribute: attributeId,
        value: valueId
      }));

      await api.post('/admin/products', {
        ...formData,
        productType: 'simple',
        price,
        originalPrice:
          formData.originalPrice !== undefined && Number.isFinite(Number(formData.originalPrice))
            ? Number(formData.originalPrice)
            : price,
        quantityAvailable,
        attributes: attributes.length > 0 ? attributes : undefined,
      });

      alert(t('messages.productCreated', { ns: 'addProduct' }));
      navigate('/products');
    } catch (error: any) {
      console.error('Error creating product:', error);
      alert(t('messages.errorCreating', { ns: 'addProduct', message: error.response?.data?.message || error.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;

    // Validate file size (max 10MB)
    const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSizeInBytes) {
      alert(t('messages.fileSizeError', { ns: 'addProduct' }));
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(t('messages.fileTypeError', { ns: 'addProduct' }));
      return;
    }

    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      // Use the correct backend upload endpoint (base URL already includes /api/v1)
      const response = await api.post('/upload-single-file', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle the response format from backend
      if (response.data.data?.fileUrl) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, response.data.data.fileUrl]
        }));
      } else {
        throw new Error(t('messages.invalidUploadResponse', { ns: 'addProduct' }));
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      alert(t('messages.uploadError', { ns: 'addProduct', message: error.response?.data?.message || error.message }));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageRemove = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleCropImage = (index: number) => {
    setImageToCrop({ url: formData.images[index], index });
    setCropModalOpen(true);
  };

  const handleCropSave = (croppedImageUrl: string) => {
    if (imageToCrop) {
      const updatedImages = [...formData.images];
      updatedImages[imageToCrop.index] = croppedImageUrl;
      setFormData(prev => ({
        ...prev,
        images: updatedImages
      }));
    }
    setCropModalOpen(false);
    setImageToCrop(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/products')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'addProduct' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'addProduct' })}</p>
        </div>
      </div>

      {/* Form */}
      <div className="card p-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('sections.basicInfo', { ns: 'addProduct' })}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.productName', { ns: 'addProduct' })} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field"
                  placeholder={t('fields.productNamePlaceholder', { ns: 'addProduct' })}
                  required
                />
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.brand', { ns: 'addProduct' })} *
                </label>
                <select
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="">{t('fields.selectBrand', { ns: 'addProduct' })}</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.name}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.category', { ns: 'addProduct' })} *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">{t('fields.selectCategory', { ns: 'addProduct' })}</option>
                  {getSelectableCategories().map((category) => (
                    <option key={category.id} value={category.id}>
                      {formatCategoryName(category)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Admin creates simple products only; variants via seller dashboard */}
              <input type="hidden" name="productType" value="simple" />
            </div>
          </div>

          {/* Product Images - Moved up */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sections.photos', { ns: 'addProduct' })}</h3>
            <p className="text-gray-600 text-sm mb-6">{t('photos.subtitle', { ns: 'addProduct' })}</p>
            
            {/* Image Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {/* Existing Images */}
              {formData.images.map((url, index) => (
                <div key={index} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={url}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                      <ImageIcon size={24} className="text-gray-400" />
                    </div>
                  </div>
                  
                  {/* Cover Label for First Image */}
                  {index === 0 && (
                    <div className="absolute bottom-2 left-2 bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
                      {t('photos.cover', { ns: 'addProduct' })}
                    </div>
                  )}
                  
                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleImageRemove(index)}
                    className="absolute -top-2 -right-2 bg-black text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-gray-800 transition-colors"
                  >
                    <X size={14} />
                  </button>
                  
                  {/* Crop Button */}
                  <button
                    type="button"
                    onClick={() => handleCropImage(index)}
                    className="absolute bottom-2 right-2 bg-white p-1 rounded shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                    title={t('photos.cropImage', { ns: 'addProduct' })}
                  >
                    <Crop size={12} className="text-gray-600" />
                  </button>
                </div>
              ))}
              
              {/* Add Photo Slot - Only show one at a time */}
              {formData.images.length < 4 && (
                <div className="relative">
                  <div 
                    className={`aspect-square bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors cursor-pointer flex flex-col items-center justify-center ${
                      dragActive ? 'border-primary-500 bg-primary-50' : ''
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('imageInput')?.click()}
                  >
                    {uploadingImage ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                    ) : (
                      <>
                        <div className="w-8 h-8 mb-2 flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
                            <circle cx="12" cy="13" r="3"/>
                            <path d="M12 10v6M9 13h6"/>
                          </svg>
                        </div>
                        <span className="text-sm text-gray-500 font-medium">{t('photos.addPhoto', { ns: 'addProduct' })}</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Hidden File Input */}
            <input
              id="imageInput"
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploadingImage}
            />

            {/* Info Text */}
            <div className="flex items-start space-x-3 text-sm text-gray-600">
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <p>
                {t('photos.infoText', { ns: 'addProduct' })}
              </p>
            </div>
          </div>

          {/* Description - Moved down */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('sections.description', { ns: 'addProduct' })}</h3>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field w-full"
              rows={6}
              placeholder={t('fields.descriptionPlaceholder', { ns: 'addProduct' })}
              required
            />
          </div>

          {/* Pricing & Inventory */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('sections.pricingInventory', { ns: 'addProduct' })}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.price', { ns: 'addProduct' })}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price || ''}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || undefined })}
                  className="input-field"
                  placeholder={t('fields.pricePlaceholder', { ns: 'addProduct' })}
                />
              </div>

              {/* Original Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.originalPrice', { ns: 'addProduct' })}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.originalPrice || ''}
                  onChange={(e) => setFormData({ ...formData, originalPrice: parseFloat(e.target.value) || undefined })}
                  className="input-field"
                  placeholder={t('fields.pricePlaceholder', { ns: 'addProduct' })}
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.quantityAvailable', { ns: 'addProduct' })}
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.quantityAvailable || ''}
                  onChange={(e) => setFormData({ ...formData, quantityAvailable: parseInt(e.target.value) || undefined })}
                  className="input-field"
                  placeholder={t('fields.quantityPlaceholder', { ns: 'addProduct' })}
                />
              </div>

              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.condition', { ns: 'addProduct' })}
                </label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value as 'Brand New' | 'Used' | 'Refurbished' })}
                  className="input-field"
                >
                  <option value="Brand New">{t('fields.brandNew', { ns: 'addProduct' })}</option>
                  <option value="Used">{t('fields.used', { ns: 'addProduct' })}</option>
                  <option value="Refurbished">{t('fields.refurbished', { ns: 'addProduct' })}</option>
                </select>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.color', { ns: 'addProduct' })}
                </label>
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="input-field"
                  placeholder={t('fields.colorPlaceholder', { ns: 'addProduct' })}
                />
              </div>
            </div>
          </div>

          {/* Category Attributes */}
          {categoryAttributes.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('sections.specifications', { ns: 'addProduct' })}</h3>
              <p className="text-sm text-gray-600 mb-6">
                {t('specifications.subtitle', { ns: 'addProduct' })}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryAttributes.map((catAttr: any) => {
                  const attribute = typeof catAttr.attribute === 'object' ? catAttr.attribute : null;
                  const attrId = attribute?._id || catAttr.attribute;
                  const values = attributeValues[attrId] || [];

                  if (!attribute) return null;

                  return (
                    <div key={attrId}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {attribute.displayName} {catAttr.isRequired && <span className="text-red-500">*</span>}
                      </label>

                      {attribute.type === 'select' && (
                        <select
                          value={selectedAttributes[attrId] || ''}
                          onChange={(e) => setSelectedAttributes({...selectedAttributes, [attrId]: e.target.value})}
                          className="input-field"
                          required={catAttr.isRequired}
                        >
                          <option value="">{t('fields.select', { ns: 'addProduct' })} {attribute.displayName}</option>
                          {values.map((value: any) => (
                            <option key={value._id} value={value._id}>
                              {value.value}
                            </option>
                          ))}
                        </select>
                      )}

                      {attribute.type === 'color' && (
                        <div className="flex flex-wrap gap-2">
                          {values.map((value: any) => (
                            <button
                              key={value._id}
                              type="button"
                              onClick={() => setSelectedAttributes({...selectedAttributes, [attrId]: value._id})}
                              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border-2 transition-all ${
                                selectedAttributes[attrId] === value._id
                                  ? 'border-custom-primary bg-custom-secondary/10'
                                  : 'border-gray-300 hover:border-gray-400'
                              }`}
                              title={value.value}
                            >
                              <div
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: value.colorHex }}
                              />
                              <span className="text-sm">{value.value}</span>
                            </button>
                          ))}
                        </div>
                      )}

                      {attribute.type === 'text' && (
                        <input
                          type="text"
                          value={selectedAttributes[attrId] || ''}
                          onChange={(e) => setSelectedAttributes({...selectedAttributes, [attrId]: e.target.value})}
                          className="input-field"
                          placeholder={`Enter ${attribute.displayName.toLowerCase()}`}
                          required={catAttr.isRequired}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Product Status */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{t('sections.status', { ns: 'addProduct' })}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Checkbox
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                label={t('statusOptions.active', { ns: 'addProduct' })}
                description={t('statusOptions.activeDesc', { ns: 'addProduct' })}
                variant="card"
              />

              <Checkbox
                id="isRecommended"
                checked={formData.isRecommended}
                onChange={(e) => setFormData({ ...formData, isRecommended: e.target.checked })}
                label={t('statusOptions.recommended', { ns: 'addProduct' })}
                description={t('statusOptions.recommendedDesc', { ns: 'addProduct' })}
                variant="card"
              />

              <Checkbox
                id="isFlash"
                checked={formData.isFlash}
                onChange={(e) => setFormData({ ...formData, isFlash: e.target.checked })}
                label={t('statusOptions.flashSale', { ns: 'addProduct' })}
                description={t('statusOptions.flashSaleDesc', { ns: 'addProduct' })}
                variant="card"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/products')}
              className="btn-outline"
            >
              {t('cancel', { ns: 'common' })}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-50"
            >
              {submitting ? t('buttons.creatingProduct', { ns: 'addProduct' }) : t('buttons.createProduct', { ns: 'addProduct' })}
            </button>
          </div>
        </form>
      </div>

      {/* Crop Modal */}
      {cropModalOpen && imageToCrop && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{t('cropModal.title', { ns: 'addProduct' })}</h3>
              <button
                onClick={() => setCropModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="relative">
                <img
                  src={imageToCrop.url}
                  alt="Crop preview"
                  className="max-w-full h-auto max-h-96 mx-auto rounded"
                />
                {/* Simple crop overlay */}
                <div className="absolute inset-4 border-2 border-white border-dashed rounded"></div>
              </div>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              <p>{t('cropModal.info', { ns: 'addProduct' })}</p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setCropModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                {t('cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={() => handleCropSave(imageToCrop.url)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {t('cropModal.applyCrop', { ns: 'addProduct' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProduct;